import { useAtom } from '@reatom/npm-react'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Numeric, Tooltip } from '@blade/ui-kit'
import { AmountFormatted } from '@root/shared/core/format'
import { DevBurnIcon } from '@root/shared/ui-kit'

import type { PairModel } from '../../atoms'
import type { BigNumber } from 'bignumber.js'

type PairInformationDevBurnProps = {
  pair: PairModel
}

export const PairInformationDevBurn: React.FC<PairInformationDevBurnProps> =
  memo(({ pair }) => {
    const [devBurnt] = useAtom(
      (ctx) => {
        const initTxCreator = ctx.spy(pair.initTxCreator)
        return initTxCreator?.quoteTokensBurned
      },
      [pair.initTxCreator],
    )
    const [devBurntPercentage] = useAtom(
      (ctx) => {
        const initTxCreator = ctx.spy(pair.initTxCreator)
        return initTxCreator?.quoteTokensBurnedPercentage
      },
      [pair.initTxCreator],
    )

    if (!devBurnt || devBurnt.isZero()) {
      return undefined
    }

    return (
      <Tooltip
        content={
          <TooltipContent
            pair={pair}
            devBurnt={devBurnt}
            devBurntPercentage={devBurntPercentage}
          />
        }
      >
        <Icon size={16} />
      </Tooltip>
    )
  })

const Icon = styled(DevBurnIcon)`
  pointer-events: auto;
  color: var(--success-9);
`

const TooltipContent = ({
  pair,
  devBurnt,
  devBurntPercentage,
}: {
  pair: PairModel
  devBurnt: BigNumber
  devBurntPercentage: BigNumber | undefined
}) => {
  const { t } = useTranslation()

  const ticker = useMemo(() => {
    if (pair.quote.ticker.length > 5) {
      return pair.quote.ticker.slice(0, 5) + '…'
    }
    return pair.quote.ticker
  }, [pair.quote.ticker])

  return (
    <ContentRoot>
      <Item>
        <Label>{t('trading.dev-burn.dev-burnt')}</Label>
        <Value>
          <AmountFormatted amount={devBurnt} />
          <Ticker> {ticker}</Ticker>
        </Value>
      </Item>
      {devBurntPercentage ? (
        <Item>
          <Label>{t('trading.dev-burn.burnt-rate')}</Label>
          <Value>
            <Numeric value={devBurntPercentage} preset="percents" />%
          </Value>
        </Item>
      ) : undefined}
    </ContentRoot>
  )
}

const ContentRoot = styled.div`
  display: grid;
  justify-content: stretch;
  gap: 4px;
  min-width: 150px;
  font-size: 12px;
`

const Item = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  justify-content: space-between;
`

const Label = styled.div`
  color: var(--text-11);
`

const Value = styled.div`
  font-weight: 500;
`

const Ticker = styled.span`
  color: var(--text-11);
  font-weight: 400;
`
